import { useState } from "react";
import { Banknote } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const STATUS = { requested: ["Processing", "text-amber-700"], paid: ["Paid", "text-emerald-700"], rejected: ["Returned to wallet", "text-ink/50"] };

export default function WithdrawPanel({ wallet, onChange }) {
  const [amount, setAmount] = useState(wallet.balance >= wallet.min_withdrawal ? wallet.balance : "");
  const [busy, setBusy] = useState(false);
  const kycOk = wallet.kyc?.status === "approved";
  const pending = wallet.withdrawals.some((w) => w.status === "requested");
  const canWithdraw = kycOk && wallet.balance >= wallet.min_withdrawal && !pending;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/customer/withdrawals", { amount: Number(amount) });
      toast.success("Withdrawal requested — paid to your bank within 3 working days");
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not request withdrawal");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-ink/10 rounded-3xl p-6" data-testid="withdraw-panel">
      <h3 className="font-serif text-xl font-bold flex items-center gap-2"><Banknote size={18} className="text-terra" /> Withdraw to bank</h3>
      <p className="text-xs text-ink/60 mt-1">Minimum {inr(wallet.min_withdrawal)}. Paid by bank transfer / UPI after KYC verification.</p>
      {!kycOk && <p className="mt-3 text-sm text-amber-700 bg-amber-50 rounded-xl px-3 py-2" data-testid="withdraw-kyc-required">Complete KYC below to unlock withdrawals.</p>}
      {pending && <p className="mt-3 text-sm text-ink/60 bg-sand rounded-xl px-3 py-2" data-testid="withdraw-pending">A withdrawal is being processed.</p>}
      <form onSubmit={submit} className="mt-4 flex gap-2">
        <input type="number" min={wallet.min_withdrawal} max={wallet.balance} value={amount} onChange={(e) => setAmount(e.target.value)} disabled={!canWithdraw} placeholder={inr(wallet.min_withdrawal)}
          className="flex-1 bg-sand border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50 disabled:opacity-50" data-testid="withdraw-amount" />
        <button type="submit" disabled={!canWithdraw || busy || !amount} data-testid="withdraw-submit" className="bg-ink text-sand text-sm font-semibold px-5 rounded-xl hover:bg-terra transition-colors disabled:opacity-40">{busy ? "…" : "Withdraw"}</button>
      </form>
      {wallet.withdrawals.length > 0 && (
        <ul className="mt-4 divide-y divide-ink/10 text-sm" data-testid="withdrawals-list">
          {wallet.withdrawals.map((w) => (
            <li key={w.id} className="py-2.5 flex items-center justify-between">
              <span className="text-ink/60">{new Date(w.created_at).toLocaleDateString("en-IN")}{w.reference && <span className="text-xs"> · ref {w.reference}</span>}</span>
              <span className="flex items-center gap-3"><span className={`text-xs font-semibold ${STATUS[w.status]?.[1]}`}>{STATUS[w.status]?.[0]}</span><span className="font-semibold">{inr(w.amount)}</span></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
